"use client";

// Section animée de la fiche Codex : les endroits où l'agent travaille pour
// vous. Calquée sur CxEverywhere (même grille, même preuve en bas de bloc), en
// bleu ciel pour distinguer Codex dans le catalogue.

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const USES = [
    { icon: "code-square-bold-duotone", t: "Cx_ew_u1_t", d: "Cx_ew_u1_d" },
    { icon: "programming-bold-duotone", t: "Cx_ew_u2_t", d: "Cx_ew_u2_d" },
    { icon: "layers-minimalistic-bold-duotone", t: "Cx_ew_u3_t", d: "Cx_ew_u3_d" },
    { icon: "code-scan-bold-duotone", t: "Cx_ew_u4_t", d: "Cx_ew_u4_d" },
    { icon: "shield-check-bold-duotone", t: "Cx_ew_u5_t", d: "Cx_ew_u5_d" },
    { icon: "wallet-bold-duotone", t: "Cx_ew_u6_t", d: "Cx_ew_u6_d" },
];

const gridContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};
const cardItem = {
    hidden: { opacity: 0, y: 16, scale: 0.96 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.45, ease: "easeOut" },
    },
};

export default function CxEverywhere() {
    const t = useTranslations();

    return (
        <section id="cx-everywhere" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Cx_ew_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Cx_ew_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-8 sm:p-12">
                {/* Glows (bleu ciel Codex) */}
                <div className="absolute -top-24 -right-20 w-[460px] h-[460px] bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* Eyebrow */}
                    <div className="inline-flex items-center gap-2 rounded-full bg-sky-500/15 ring-1 ring-sky-400/30 px-3 py-1 mb-4">
                        <SolarIcon
                            name="monitor-smartphone-bold-duotone"
                            className="w-3.5 h-3.5 text-sky-300"
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-300">
                            {t("Cx_ew_eyebrow")}
                        </span>
                    </div>

                    {/* Headline */}
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-[1.12] tracking-tight mb-8 max-w-[640px]">
                        {t.rich("Cx_ew_headline", {
                            highlight: (chunks) => (
                                <span className="bg-gradient-to-r from-sky-300 to-cyan-200 bg-clip-text text-transparent">
                                    {chunks}
                                </span>
                            ),
                        })}
                    </h3>

                    {/* Grille des usages */}
                    <motion.div
                        variants={gridContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5"
                    >
                        {USES.map((u) => (
                            <motion.div
                                key={u.t}
                                variants={cardItem}
                                className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 hover:bg-white/[0.07] transition-colors duration-200"
                            >
                                <div className="w-11 h-11 rounded-xl bg-sky-500/15 ring-1 ring-sky-400/30 flex items-center justify-center mb-4">
                                    <SolarIcon
                                        name={u.icon}
                                        className="w-5 h-5 text-sky-300"
                                    />
                                </div>
                                <h4 className="text-base font-semibold text-white mb-1 leading-tight">
                                    {t(u.t)}
                                </h4>
                                <p className="text-sm text-gray-300/80 leading-relaxed mb-0">
                                    {t(u.d)}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Preuve (texte, pas de logos tiers) */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                        className="mt-8 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-5 py-5"
                    >
                        <div className="flex items-start gap-3">
                            <SolarIcon
                                name="verified-check-bold"
                                className="w-5 h-5 text-sky-300 shrink-0 mt-0.5"
                            />
                            <p className="text-sm text-gray-300/90 leading-relaxed mb-0">
                                {t.rich("Cx_ew_proof", {
                                    b: (chunks) => (
                                        <strong className="text-white font-semibold">
                                            {chunks}
                                        </strong>
                                    ),
                                })}
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
